import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, PhoneOffIcon } from 'lucide-react';
import { usePrefersReducedMotion } from './motion';

/* Makes the Leak section's claim literal: a phone rings out, goes to
   voicemail, and the tap lands on the next search result instead. A ring
   counter ticks 1-4 (matching the section's own "rings 4x" stat) before the
   missed state, then a small animated arrow hands the tap to a competitor
   row. Loops on a slow interval so a visitor who lingers sees the whole
   story, not just the end state; reduced motion skips straight to the
   missed/competitor end state instead of cycling. */
export function MissedCallVisual() {
  const reducedMotion = usePrefersReducedMotion();
  const [ring, setRing] = useState(0); // 0-4, 4 = missed

  useEffect(() => {
    if (reducedMotion) {
      setRing(4);
      return;
    }
    const id = setInterval(() => setRing(r => r >= 4 ? 0 : r + 1), 700);
    return () => clearInterval(id);
  }, [reducedMotion]);
  const missed = ring >= 4;
  return <div className="rounded-2xl border border-[#1c1c1c] bg-[#0a0a0a] p-6 sm:p-7" data-id="element-775" data-mp="tfhk81s4rqvm">
      <div className="flex items-center gap-5" data-id="element-776" data-mp="oihfbu70ou63">
        <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-[#232323] bg-[#111111]" data-id="element-777" data-mp="1385hexn0vwuv">
          {!missed && !reducedMotion && <motion.span key={ring} className="absolute inset-0 rounded-full bg-red-500/20" initial={{
          scale: 1,
          opacity: 0.7
        }} animate={{
          scale: 1.7,
          opacity: 0
        }} transition={{
          duration: 0.65,
          ease: 'easeOut'
        }} aria-hidden="true" data-id="element-778" data-mp="aiq3o03z3pv9" />}
          {missed ? <PhoneOffIcon className="h-6 w-6 text-neutral-600" aria-hidden="true" data-id="element-779" data-mp="fgroaph1rx8u" /> : <PhoneIcon className="h-6 w-6 text-red-400" aria-hidden="true" data-id="element-780" data-mp="1v0we198mbnkn" />}
        </div>
        <div data-id="element-781" data-mp="1qzau53xo290y">
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500" data-id="element-782" data-mp="1qzko12lu10bx">
            Incoming call
          </p>
          <p className="mt-1 text-sm font-medium text-neutral-300" data-id="element-783" data-mp="skm3uysqeuka">
            {missed ? 'No answer, sent to voicemail' : `Ringing... (${ring + 1} of 4)`}
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-2 border-t border-[#1c1c1c] pt-6" data-id="element-784" data-mp="11mli5jcd3kjj">
        <div className="flex items-center justify-between rounded-lg border border-[#1c1c1c] bg-[#0d0d0d] px-4 py-3 opacity-50" data-id="element-785" data-mp="4vt38p15zyjc">
          <span className="text-xs text-neutral-400" data-id="element-786" data-mp="9u4dh0vdgb23">Your Business</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600" data-id="element-787" data-mp="dwixvpeovivh">
            No answer
          </span>
        </div>
        <motion.div animate={missed ? {
        opacity: 1
      } : {
        opacity: 0.35
      }} transition={{
        duration: 0.4
      }} className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3" data-id="element-788" data-mp="io0ygccsvhfx">
          <span className="text-xs text-neutral-200" data-id="element-789" data-mp="3esjn2uav679g">Next Search Result</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-emerald-500" data-id="element-790" data-mp="3eqfi0918b27l">
            {missed ? 'Booked the job' : 'Waiting'}
          </span>
        </motion.div>
      </div>
    </div>;
}